import { ListItem, ListItemButton, ListItemIcon, ListItemText, Badge, Tooltip } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { useNavigation } from '../../../contexts/NavigationContext';
import type { NavItem } from '../../../types/navigation';
import { transitions } from '../../../theme/tokens';

interface SidebarNavItemProps {
  item: NavItem;
}

export function SidebarNavItem({ item }: SidebarNavItemProps) {
  const { isCollapsed } = useNavigation();
  const location = useLocation();

  const isActive = location.pathname === item.path;

  const itemButton = (
    <ListItemButton
      component={NavLink}
      to={item.path}
      disabled={item.disabled}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        minHeight: 40,
        px: isCollapsed ? 2.5 : 2,
        py: 0.75,
        borderRadius: 1,
        mx: 1,
        mb: 0.25,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        backgroundColor: isActive ? 'action.selected' : 'transparent',
        '&:hover': {
          backgroundColor: isActive ? 'action.selected' : 'action.hover',
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
        transition: `background-color ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: isCollapsed ? 0 : 32,
          justifyContent: 'center',
          color: isActive ? 'primary.main' : 'text.secondary',
          transition: `color ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
        }}
      >
        {item.badge !== undefined ? (
          <Badge
            badgeContent={item.badge}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.625rem',
                minWidth: 16,
                height: 16,
              },
            }}
          >
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </ListItemIcon>

      {!isCollapsed && (
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '0.8125rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? 'primary.main' : 'text.primary',
            noWrap: true,
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      {isCollapsed ? (
        <Tooltip title={item.label} placement="right" arrow>
          {itemButton}
        </Tooltip>
      ) : (
        itemButton
      )}
    </ListItem>
  );
}
